import { PhotoTrigger } from './photo-viewer.tsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { quickQuoteApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { reencodePhoto } from '../hooks/use-walkthrough-capture.ts';
import { Button, classes } from './ui.tsx';
import { canReadQuickQuotePhotoAgain, type QuickQuotePhoto } from '@shared/quick-quote.ts';

/**
 * Photos for a Quick Quote. Each one is redrawn on the device (which also
 * leaves its location data behind), uploaded, and read on the server as it
 * arrives; the Quick Quote then starts with the ones whose read finished.
 * Photos already waiting from an earlier visit to the page come back too.
 */

/** How often a photo still being read is asked about. */
const READ_POLL_MS = 2_000;
/** How many more times, and how far apart, a failed lookup of earlier photos is tried. */
const RESTORE_RETRIES = 3;
const RESTORE_RETRY_MS = 2_000;

export interface DraftQuickQuotePhoto {
  /** Also the upload's `clientPhotoId`, so a retried upload is stored once. */
  key: string;
  /** The redrawn photo, kept so a failed upload can be tried again. */
  blob: Blob | null;
  /** A local preview, or the stored photo's address once there is no local copy. */
  previewUrl: string | null;
  state: 'uploading' | 'uploaded' | 'failed';
  photo: QuickQuotePhoto | null;
  error: string | null;
  /** Being sent back to be read; counts as still reading until the server answers. */
  readingAgain?: boolean;
  /** On its way out: not to be started with, and the start waits for it. */
  removing?: boolean;
}

export interface QuickQuotePhotoState {
  photos: DraftQuickQuotePhoto[];
  /**
   * Earlier photos are still being looked up, or a batch is being added:
   * another batch now would count room from a list about to change.
   */
  busy: boolean;
  /** Too many chosen at once: how many fit. */
  overflowed: number | null;
  /** A photo is still uploading or being read. */
  pending: boolean;
  /**
   * Every lookup of earlier photos failed: starting now could leave out ones
   * already read and paid for, so the start waits for `retryRestore`.
   */
  restoreFailed: boolean;
  retryRestore(): void;
  /**
   * Uploads that failed but may have reached the server (and been read, and
   * charged): each is tried again or removed before a Quick Quote starts.
   */
  failedUploads: number;
  /** The photos to start with, in order: every upload whose read finished. */
  readyIds: string[];
  /** How many of those read cleanly, so they reach the draft. */
  readCount: number;
  add(files: readonly File[]): Promise<void>;
  retry(key: string): void;
  /** Sends a photo skipped for a reason that can clear back to be read. */
  readAgain(key: string): Promise<void>;
  remove(key: string): Promise<void>;
  /** After a Quick Quote starts with them: they belong to it now. */
  clear(): void;
}

function newPhotoKey(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof ApiRequestError && caught.message ? caught.message : fallback;
}

/**
 * `beforeUpload` runs once per batch before anything leaves the device; the
 * Quick Quote page uses it to have the AI notice accepted first, because a
 * photo is read the moment it arrives.
 */
export function useQuickQuotePhotos(
  maxPhotos: number,
  beforeUpload: () => Promise<void>,
  enabled: boolean,
): QuickQuotePhotoState {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<DraftQuickQuotePhoto[]>([]);
  const [overflowed, setOverflowed] = useState<number | null>(null);
  const photosRef = useRef(photos);
  photosRef.current = photos;
  const mounted = useRef(true);

  const update = useCallback((key: string, change: Partial<DraftQuickQuotePhoto>) => {
    if (!mounted.current) return;
    setPhotos((current) => current.map((entry) => (entry.key === key ? { ...entry, ...change } : entry)));
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      for (const entry of photosRef.current) {
        if (entry.blob && entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      }
    };
  }, []);

  // Photos left waiting by an earlier visit, so they can be used or removed.
  // A lookup that fails is asked again a few times before giving up, since
  // the photos it would find still count against the limit; after that the
  // start waits until a retry finds them.
  const [restoring, setRestoring] = useState(false);
  const [restoreFailed, setRestoreFailed] = useState(false);
  const [restoreRound, setRestoreRound] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setRestoring(true);
    setRestoreFailed(false);
    const attempt = (left: number) => {
      quickQuoteApi
        .waitingPhotos()
        .then(({ photos: waiting }) => {
          if (cancelled) return;
          setRestoring(false);
          if (waiting.length === 0) return;
          setPhotos((current) => {
            const known = new Set(current.map((entry) => entry.photo?.id ?? entry.key));
            const restored = waiting
              .filter((photo) => !known.has(photo.id) && !known.has(photo.clientPhotoId))
              .map((photo): DraftQuickQuotePhoto => ({
                key: photo.clientPhotoId,
                blob: null,
                previewUrl: photo.mediaUrl,
                state: 'uploaded',
                photo,
                error: null,
              }));
            return [...restored, ...current];
          });
        })
        .catch(() => {
          if (cancelled) return;
          if (left > 0) {
            timer = setTimeout(() => attempt(left - 1), RESTORE_RETRY_MS);
            return;
          }
          setRestoring(false);
          setRestoreFailed(true);
        });
    };
    attempt(RESTORE_RETRIES);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      setRestoring(false);
    };
  }, [enabled, restoreRound]);
  const retryRestore = useCallback(() => setRestoreRound((value) => value + 1), []);

  const upload = useCallback(
    async (key: string, blob: Blob) => {
      update(key, { state: 'uploading', error: null });
      try {
        const photo = await quickQuoteApi.uploadPhoto(blob, key);
        update(key, { state: 'uploaded', photo });
      } catch (caught) {
        update(key, { state: 'failed', error: errorMessage(caught, t('quickQuote.photos.uploadFailed')) });
      }
    },
    [t, update],
  );

  // Ask after every photo still being read, one round at a time. Each round
  // that comes back moves `round` on, so a read slower than one round is
  // asked about again rather than left on "Reading…".
  const [round, setRound] = useState(0);
  const reading = photos
    .filter((entry) => entry.state === 'uploaded' && entry.photo?.status === 'reading')
    .map((entry) => entry.photo!.id)
    .join(',');
  useEffect(() => {
    if (!reading) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void Promise.all(
        reading.split(',').map((photoId) =>
          quickQuoteApi
            .photo(photoId)
            .then((photo) => ({ photoId, photo, gone: false }))
            .catch((caught: unknown) => ({
              photoId,
              photo: null,
              // Removed in another tab, or swept: nothing left to wait for.
              gone: caught instanceof ApiRequestError && caught.status === 404,
            })),
        ),
      ).then((answers) => {
        if (cancelled || !mounted.current) return;
        const gone = new Set(answers.filter((answer) => answer.gone).map((answer) => answer.photoId));
        for (const entry of photosRef.current) {
          if (entry.photo && gone.has(entry.photo.id) && entry.blob && entry.previewUrl) {
            URL.revokeObjectURL(entry.previewUrl);
          }
        }
        setPhotos((current) =>
          current
            .filter((entry) => !(entry.photo && gone.has(entry.photo.id)))
            .map((entry) => {
              const answer = answers.find((candidate) => candidate.photoId === entry.photo?.id);
              return answer?.photo ? { ...entry, photo: answer.photo } : entry;
            }),
        );
        setRound((value) => value + 1);
      });
    }, READ_POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [reading, round]);

  const addBatch = useCallback(
    async (chosen: readonly File[]) => {
      try {
        await beforeUpload();
      } catch {
        return;
      }
      // One canvas for the batch: resizing one is cheap, and fifteen is what
      // exhausts a phone (the walkthrough's library import does the same).
      const canvas = document.createElement('canvas');
      for (const file of chosen) {
        if (!mounted.current) return;
        const key = newPhotoKey();
        const blob = await reencodePhoto(file, canvas);
        // Redrawing a large photo takes a while: the page may be gone by now,
        // and then nothing is sent (it would be stored, read and charged).
        if (!mounted.current) return;
        if (!blob) {
          setPhotos((current) => [
            ...current,
            {
              key,
              blob: null,
              previewUrl: null,
              state: 'failed',
              photo: null,
              error: t('quickQuote.photos.notAPhoto'),
            },
          ]);
          continue;
        }
        setPhotos((current) => [
          ...current,
          { key, blob, previewUrl: URL.createObjectURL(blob), state: 'uploading', photo: null, error: null },
        ]);
        await upload(key, blob);
      }
    },
    [beforeUpload, t, upload],
  );

  // One batch at a time: a second pick while the first is still on its way
  // would count room from the same list and go past the limit.
  const addingRef = useRef(false);
  const [adding, setAdding] = useState(false);
  const add = useCallback(
    async (files: readonly File[]) => {
      if (addingRef.current) return;
      const room = Math.max(0, maxPhotos - photosRef.current.length);
      setOverflowed(files.length > room ? room : null);
      const chosen = files.slice(0, room);
      if (chosen.length === 0) return;
      addingRef.current = true;
      setAdding(true);
      try {
        await addBatch(chosen);
      } finally {
        addingRef.current = false;
        if (mounted.current) setAdding(false);
      }
    },
    [addBatch, maxPhotos],
  );

  const retry = useCallback(
    (key: string) => {
      const entry = photosRef.current.find((candidate) => candidate.key === key);
      if (entry?.state === 'failed' && entry.blob && !entry.removing) void upload(key, entry.blob);
    },
    [upload],
  );

  const readAgain = useCallback(
    async (key: string) => {
      const entry = photosRef.current.find((candidate) => candidate.key === key);
      if (!entry?.photo || entry.readingAgain || entry.removing || !canReadQuickQuotePhotoAgain(entry.photo)) return;
      update(key, { readingAgain: true, error: null });
      try {
        const photo = await quickQuoteApi.readPhotoAgain(entry.photo.id);
        update(key, { photo, readingAgain: false });
      } catch (caught) {
        update(key, { readingAgain: false, error: errorMessage(caught, t('quickQuote.photos.readAgainFailed')) });
      }
    },
    [t, update],
  );

  const remove = useCallback(
    async (key: string) => {
      const entry = photosRef.current.find((candidate) => candidate.key === key);
      if (!entry || entry.state === 'uploading' || entry.removing) return;
      // Marked before the server is asked, so a start meanwhile waits rather
      // than naming a photo that is going.
      update(key, { removing: true, error: null });
      const kept = (caught: unknown) =>
        update(key, { removing: false, error: errorMessage(caught, t('quickQuote.photos.removeFailed')) });
      // An upload whose answer was lost may still have been stored (and read):
      // find it by its key among the waiting photos before letting it go.
      let photoId = entry.photo?.id ?? null;
      if (!photoId && entry.blob) {
        try {
          const { photos: waiting } = await quickQuoteApi.waitingPhotos();
          photoId = waiting.find((photo) => photo.clientPhotoId === key)?.id ?? null;
        } catch (caught) {
          kept(caught);
          return;
        }
      }
      if (photoId) {
        try {
          await quickQuoteApi.removePhoto(photoId);
        } catch (caught) {
          // Already gone is what was asked for; anything else stays on screen.
          if (!(caught instanceof ApiRequestError && caught.status === 404)) {
            kept(caught);
            return;
          }
        }
      }
      if (entry.blob && entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      if (mounted.current) setPhotos((current) => current.filter((candidate) => candidate.key !== key));
    },
    [t, update],
  );

  const clear = useCallback(() => {
    for (const entry of photosRef.current) {
      if (entry.blob && entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    }
    setPhotos([]);
    setOverflowed(null);
  }, []);

  // Earlier photos still being looked up count too: starting now would leave
  // out ones already read and paid for.
  const pending =
    restoring ||
    photos.some(
      (entry) =>
        entry.state === 'uploading' ||
        entry.readingAgain === true ||
        entry.removing === true ||
        (entry.state === 'uploaded' && entry.photo?.status === 'reading'),
    );
  const ready = photos.filter(
    (entry) => entry.state === 'uploaded' && entry.photo && entry.photo.status !== 'reading' && !entry.removing,
  );
  return {
    photos,
    busy: restoring || adding,
    overflowed,
    pending,
    restoreFailed,
    retryRestore,
    failedUploads: photos.filter((entry) => entry.state === 'failed' && entry.blob).length,
    readyIds: ready.map((entry) => entry.photo!.id),
    readCount: ready.filter((entry) => entry.photo!.status === 'read').length,
    add,
    retry,
    readAgain,
    remove,
    clear,
  };
}

/** One line on what became of a photo. */
function photoCaption(t: (key: string, options?: Record<string, unknown>) => string, photo: QuickQuotePhoto): string {
  switch (photo.status) {
    case 'reading':
      return t('quickQuote.photos.reading');
    case 'read':
      return photo.descriptor || t('quickQuote.photos.read');
    case 'hidden':
      return t(`quickQuote.photos.hidden.${photo.hiddenReason ?? 'irrelevant'}`);
    case 'unreadable':
      return t('quickQuote.photos.unreadable');
    case 'skipped':
      return t(
        photo.skipReason === 'limit' ||
          photo.skipReason === 'daily_cap' ||
          photo.skipReason === 'too_large' ||
          photo.skipReason === 'unavailable'
          ? `quickQuote.photos.skipped.${photo.skipReason}`
          : 'quickQuote.photos.skipped.generic',
      );
  }
}

function PhotoThumb({
  src,
  hidden,
  alt,
  contentType,
}: {
  src: string | null;
  hidden: boolean;
  alt: string;
  contentType?: string;
}) {
  const { t } = useTranslation();
  return src ? (
    <PhotoTrigger
      src={src}
      alt={alt}
      contentType={contentType}
      group="quick-quote-photos"
      notice={hidden ? t('photoQuotes.photos.hiddenNotice') : undefined}
      className="block w-full"
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={classes('aspect-square w-full rounded object-cover', hidden && 'blur-md')}
      />
    </PhotoTrigger>
  ) : (
    <div className="aspect-square w-full rounded bg-[color:var(--color-surface)]" aria-hidden="true" />
  );
}

export function QuickQuotePhotoPicker({
  state,
  maxPhotos,
  disabled,
}: {
  state: QuickQuotePhotoState;
  maxPhotos: number;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const full = state.photos.length >= maxPhotos;

  return (
    <div className="mb-4 rounded-md border border-[color:var(--color-line)] p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium">{t('quickQuote.photos.title')}</p>
          <p className="text-xs text-[color:var(--color-muted)]">{t('quickQuote.photos.hint', { max: maxPhotos })}</p>
        </div>
        <Button className="text-sm" disabled={disabled || full || state.busy} onClick={() => input.current?.click()}>
          {state.photos.length > 0 ? t('quickQuote.photos.addMore') : t('quickQuote.photos.add')}
        </Button>
        <input
          ref={input}
          type="file"
          accept="image/*"
          multiple
          hidden
          data-testid="quick-quote-photo-input"
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            // Cleared so the same photo can be chosen again after a removal.
            event.target.value = '';
            void state.add(files);
          }}
        />
      </div>
      {state.restoreFailed ? (
        <p className="mt-2 text-xs" role="status">
          {t('quickQuote.photos.restoreFailed')}{' '}
          <button type="button" className="underline" disabled={disabled} onClick={state.retryRestore}>
            {t('quickQuote.photos.retry')}
          </button>
        </p>
      ) : null}
      {state.overflowed !== null ? (
        <p className="mt-2 text-xs text-[color:var(--color-muted)]" role="status">
          {t('quickQuote.photos.tooMany', { max: maxPhotos })}
        </p>
      ) : null}
      {state.photos.length > 0 ? (
        <ul className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
          {state.photos.map((entry, index) => {
            const caption =
              entry.state === 'uploading'
                ? t('quickQuote.photos.uploading')
                : entry.state === 'failed' || !entry.photo
                  ? (entry.error ?? t('quickQuote.photos.uploadFailed'))
                  : photoCaption(t, entry.photo);
            return (
              <li key={entry.key} className="text-xs">
                <PhotoThumb
                  src={entry.previewUrl}
                  contentType={entry.blob?.type}
                  hidden={entry.photo?.status === 'hidden'}
                  alt={t('quickQuote.photos.alt', { number: index + 1 })}
                />
                <p className="mt-1 line-clamp-2" aria-live="polite">
                  {caption}
                </p>
                {entry.state === 'uploaded' && entry.error ? (
                  <p className="text-[color:var(--color-danger)]">{entry.error}</p>
                ) : null}
                <div className="mt-1 flex flex-wrap gap-2">
                  {entry.state === 'failed' && entry.blob ? (
                    <button
                      type="button"
                      className="underline"
                      disabled={disabled}
                      onClick={() => state.retry(entry.key)}
                    >
                      {t('quickQuote.photos.retry')}
                    </button>
                  ) : null}
                  {entry.state === 'uploaded' && entry.photo && canReadQuickQuotePhotoAgain(entry.photo) ? (
                    <button
                      type="button"
                      className="underline"
                      disabled={disabled || entry.readingAgain === true}
                      onClick={() => void state.readAgain(entry.key)}
                    >
                      {t('quickQuote.photos.readAgain')}
                    </button>
                  ) : null}
                  {entry.state !== 'uploading' ? (
                    <button
                      type="button"
                      className="underline"
                      disabled={disabled || entry.removing === true}
                      aria-label={t('quickQuote.photos.removeLabel', { number: index + 1 })}
                      onClick={() => void state.remove(entry.key)}
                    >
                      {t('quickQuote.photos.remove')}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/** The photos a started Quick Quote holds, as the draft saw them. */
export function QuickQuoteSessionPhotos({ photos }: { photos: readonly QuickQuotePhoto[] }) {
  const { t } = useTranslation();
  if (photos.length === 0) return null;
  return (
    <div className="mb-5 rounded-md border border-[color:var(--color-line)] p-3">
      <h3 className="mb-2 font-semibold">{t('quickQuote.photos.sessionTitle', { count: photos.length })}</h3>
      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {photos.map((photo, index) => (
          <li key={photo.id} className="text-xs">
            <PhotoThumb
              src={photo.mediaUrl}
              hidden={photo.status === 'hidden'}
              alt={t('quickQuote.photos.alt', { number: index + 1 })}
            />
            <p className="mt-1 line-clamp-2">{photoCaption(t, photo)}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
